import { WHATSAPP_TEMPLATES_PAGE_KEY } from './constants';
import type { TTranslations } from './types';

export const en: TTranslations = {
  [WHATSAPP_TEMPLATES_PAGE_KEY]: {
    TABLE_TITLE: 'Templates',
    IMPORT_TEMPLATE_BUTTON: 'Import template',
    PAGE_DESCRIPTION:
      'You create and approve the templates in WhatsApp Manager. Here you import them and choose the Crehana datum that fills each variable.',
    MANAGER_LINK: 'Open WhatsApp Manager',
    MANAGER_LINK_NEW_TAB: '(opens in a new tab)',
    COLUMN_TEMPLATE: 'Template',
    COLUMN_IN_WHATSAPP: 'In WhatsApp',
    COLUMN_META_STATUS: 'Status',
    COLUMN_CATEGORY: 'Category',
    COLUMN_VARIABLES: 'Variables',
    COLUMN_ACTIONS: 'Actions',
    PRODUCT_SELECTOR_LABEL: 'Product',
    PRODUCT_UNAVAILABLE_TAG: 'Coming soon',
    PRODUCT_RECRUITMENT: 'Recruitment',
    PRODUCT_ENGAGEMENT: 'Employee Engagement',
    PRODUCT_LEARNING: 'Learning',
    PRODUCT_PAYROLL: 'Payroll',
    PRODUCT_PERFORMANCE: 'Performance',
    PRODUCT_PERSONAS_HRIS: 'People HRIS',
    SEARCH_PLACEHOLDER: 'Search by alias or WhatsApp name',
    FILTER_META_STATUS_LABEL: 'Status',
    FILTER_META_STATUS_ALL_OPTION: 'All',
    FILTER_META_STATUS_CLEAR: 'Clear the status filter',
    FILTER_INCOMPLETE_LABEL: 'Variables with no datum',
    FILTER_CHIP_STATUS: 'Status: {{status}}',
    FILTER_CHIP_REMOVE: 'Remove filter {{filter}}',
    FILTER_CLEAR_ALL: 'Clear all',
    PAGE_SIZE_LABEL: 'Rows per page',
    SORT_BY: 'Sort by {{column}}',
    SORT_ASCENDING: 'Sort ascending',
    SORT_DESCENDING: 'Sort descending',
    PAGINATION_LABEL: 'Pagination',
    PAGINATION_PREVIOUS: 'Previous page',
    PAGINATION_NEXT: 'Next page',
    PAGINATION_PAGE: 'Page {{page}}',
    PAGINATION_PAGE_OF: 'Page {{page}} of {{total}}',
    PAGINATION_OF_TOTAL: 'of {{total}}',
    NO_RESULTS_TITLE: 'No template matches',
    NO_RESULTS_DESCRIPTION: 'Try another search or remove a filter.',
    NO_RESULTS_CLEAR: 'Clear filters',
    LAST_SYNC_LABEL: 'Last sync: {{date}}',
    LAST_SYNC_NEVER: 'Last sync: no data',
    SYNC_ACTION_LABEL: 'Sync with WhatsApp',
    SYNC_ACTION_TOOLTIP: 'Sync with WhatsApp. Last time: {{date}}',
    SYNC_ACTION_SYNCING_LABEL: 'Syncing...',
    SYNC_TOAST_TITLE: 'Templates synced with WhatsApp',
    SYNC_TOAST_DESCRIPTION:
      '{{updated}} updated · {{unchanged}} unchanged · {{missing}} no longer in WhatsApp · {{outdated}} with an outdated mapping',
    SYNC_ERROR_TITLE: 'Could not sync with WhatsApp',
    SYNC_ERROR_META_NOT_CONFIGURED:
      'This environment has no WhatsApp connection set up.',
    SYNC_ERROR_META_ACCESS_DENIED:
      'WhatsApp refused Crehana access to its templates.',
    SYNC_ERROR_META_UNAVAILABLE:
      'WhatsApp did not answer. Try again in a few minutes.',
    SYNC_ERROR_GENERIC: 'Try again.',
    SYNC_RESULT_TITLE: 'Check these templates',
    SYNC_RESULT_MISSING: 'No longer in WhatsApp: {{slugs}}',
    SYNC_RESULT_OUTDATED:
      'Their message changed in WhatsApp. Edit their mapping before the next send: {{slugs}}',
    VARIABLES_COUNT: '{{mapped}} of {{total}} with a datum',
    VARIABLES_NONE: 'No variables',
    ROW_MENU_TRIGGER_LABEL: 'Actions for {{alias}}',
    ROW_MENU_VIEW: 'View detail',
    ROW_MENU_EDIT: 'Edit variable data',
    ROW_MENU_REMOVE: 'Delete from Crehana',
    DETAIL_COPY_SLUG: 'Copy slug',
    DETAIL_SLUG_COPIED: 'Slug copied',
    DETAIL_MORE_ACTIONS: 'More actions',
    DETAIL_CLOSE: 'Close',
    DETAIL_MESSAGE_TITLE: 'Message',
    DETAIL_MESSAGE_VIEW_LABEL: 'How to show the variables',
    DETAIL_MESSAGE_WITH_EXAMPLES: 'With examples',
    DETAIL_MESSAGE_WITH_VARIABLES: 'With variables',
    DETAIL_MESSAGE_NOTE:
      'Approximate preview. WhatsApp applies its own format.',
    DETAIL_MESSAGE_PENDING_TITLE:
      'WhatsApp has not sent the text of this template yet',
    DETAIL_MESSAGE_PENDING_DESCRIPTION:
      'The text and its variables arrive when Meta approves the template. Then, sync.',
    DETAIL_SYNC_NOW: 'Sync now',
    DETAIL_VARIABLES_TITLE: 'Datum of each variable',
    DETAIL_VARIABLE_NO_DATUM: 'No datum',
    DETAIL_VARIABLE_ASSIGN: 'Assign',
    DETAIL_VARIABLE_EXAMPLE: 'E.g.: {{example}}',
    DETAIL_VARIABLES_BLOCKED:
      'Crehana cannot send this template until each variable has a datum.',
    DETAIL_VARIABLES_NONE: 'The message has no variables.',
    DETAIL_META_TITLE: 'WhatsApp data',
    DETAIL_META_NAME: 'WhatsApp name',
    DETAIL_META_LANGUAGE: 'Language',
    DETAIL_META_CATEGORY: 'Category',
    DETAIL_META_ID: 'WhatsApp ID',
    DETAIL_META_LAST_SYNC: 'Last sync',
    DETAIL_META_NOTE:
      'Read only. To change them, edit the template in WhatsApp Manager and sync.',
    DETAIL_ASSIGN_DATA: 'Assign data',
    REMOVE_DIALOG_TITLE: 'Remove from the registry',
    REMOVE_DIALOG_DESCRIPTION:
      'The template stays in WhatsApp. This only removes it from the Crehana registry.',
    REMOVE_DIALOG_DETAIL:
      '«{{alias}}» ({{slug}}) leaves the registry and can no longer be sent. Nothing changes in WhatsApp.',
    REMOVE_DIALOG_USED_BY_PRODUCT: 'The {{product}} product uses it.',
    REMOVE_DIALOG_CONFIRM: 'Remove',
    REMOVE_DIALOG_CANCEL: 'Cancel',
    REMOVE_TOAST_DONE: 'Template removed from the registry. Slug: {{slug}}',
    REMOVE_TOAST_ERROR: 'Could not remove the template',
    REMOVE_TOAST_ERROR_DESCRIPTION: 'Try again.',
    EMPTY_STATE_TITLE: '{{product}} has no templates yet',
    EMPTY_STATE_DESCRIPTION:
      'Import a template that WhatsApp Manager approved to use it from Crehana.',
    ERROR_STATE_TITLE: 'We could not load the templates',
    ERROR_STATE_DESCRIPTION: 'Check your connection and try again.',
    ERROR_STATE_RETRY: 'Retry',
    META_STATUS_APPROVED: 'Approved',
    META_STATUS_PENDING: 'In review',
    META_STATUS_REJECTED: 'Rejected',
    META_STATUS_PAUSED: 'Paused',
    CATEGORY_MARKETING: 'Marketing',
    CATEGORY_UTILITY: 'Utility',
    CATEGORY_AUTHENTICATION: 'Authentication',
    ROUTE_LOADING_LABEL: 'Loading the page',
    SEND_TEST_BUTTON: 'Test a send',
    SEND_TEST_DISABLED_REASON:
      'You can test only approved templates that have a datum for every variable.',
    SEND_TEST_TITLE: 'Test a WhatsApp send',
    SEND_TEST_DESCRIPTION:
      'Sends a real message with send_whatsapp, the same operation that the products use. Each recipient gets the message.',
    SEND_TEST_CLOSE: 'Close',
    SEND_TEST_TEMPLATE_LABEL: 'Template (template_slug)',
    SEND_TEST_TEMPLATE_PLACEHOLDER: 'Choose a template',
    SEND_TEST_TEMPLATE_OPTION: '{{alias}} · {{slug}}',
    SEND_TEST_ORGANIZATION_LABEL:
      'Organization ID (centralized_organization_id)',
    SEND_TEST_VARIABLES_TITLE: 'Variables (variables)',
    SEND_TEST_VARIABLES_CHOOSE_TEMPLATE:
      'Choose a template to see its variables.',
    SEND_TEST_VARIABLES_NONE:
      'This template needs no variables from the product.',
    SEND_TEST_VARIABLES_DESCRIPTION: 'Values for all recipients.',
    SEND_TEST_VARIABLE_REQUIRED: '{{key}} (required)',
    SEND_TEST_PROFILE_VARIABLES:
      'From the recipient profile: {{keys}}. The recipient must be a member of the organization.',
    SEND_TEST_RECIPIENTS_TITLE: 'Recipients (recipients)',
    SEND_TEST_RECIPIENT_TITLE: 'Recipient {{index}}',
    SEND_TEST_RECIPIENT_ADD: 'Add recipient',
    SEND_TEST_RECIPIENT_REMOVE: 'Remove recipient {{index}}',
    SEND_TEST_USER_ID_LABEL: 'User ID (centralized_user_id)',
    SEND_TEST_PHONE_LABEL: 'Phone (phone)',
    SEND_TEST_PHONE_PLACEHOLDER: '+51987654321',
    SEND_TEST_COUNTRY_LABEL: 'Country (country_iso_code)',
    SEND_TEST_COUNTRY_PLACEHOLDER: 'PE',
    SEND_TEST_OVERRIDES_TRIGGER: 'Variables for this recipient only',
    SEND_TEST_OVERRIDES_DESCRIPTION:
      'Optional. A value here replaces the common value for this recipient.',
    SEND_TEST_SOURCE_TITLE: 'Source (source)',
    SEND_TEST_SOURCE_DESCRIPTION:
      'Optional. If you fill in target_type or target_id, also fill in app_name.',
    SEND_TEST_APP_NAME_LABEL: 'app_name',
    SEND_TEST_TARGET_TYPE_LABEL: 'target_type',
    SEND_TEST_TARGET_ID_LABEL: 'target_id',
    SEND_TEST_CANCEL: 'Cancel',
    SEND_TEST_SUBMIT: 'Send',
    SEND_TEST_TOAST_TITLE: 'Send queued',
    SEND_TEST_TOAST_DESCRIPTION:
      'ID: {{sendId}} · Status: {{status}} · Recipients: {{count}}',
    SEND_TEST_ERROR_TITLE: 'The send did not happen',
    SEND_TEST_ERROR_NETWORK: 'The server did not answer.',
    SEND_TEST_ERROR_ISSUE: '{{code}}: {{message}}',
    SEND_TEST_ERROR_ISSUE_FIELD: '{{code}} · {{field}}: {{message}}',
    SEND_TEST_ERROR_REQUIRED: 'Fill in this field.',
    SEND_TEST_ERROR_INTEGER: 'Type numbers only.',
    SEND_TEST_ERROR_PHONE:
      'Type the number with + and the country code, for example +51987654321.',
    SEND_TEST_ERROR_RECIPIENTS_MIN: 'Add at least one recipient.',
    SEND_TEST_ERROR_RECIPIENTS_MAX: 'Send to {{max}} recipients at most.',
    SEND_TEST_ERROR_MAX_LENGTH: 'Type {{max}} characters at most.',
    SEND_TEST_ERROR_APP_NAME_REQUIRED:
      'Fill in app_name when you send target_type or target_id.',
    SEND_TEST_ERROR_VARIABLE_REQUIRED:
      'Fill in this value here or in each recipient.',
  },
};
